import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Icon } from '@scale/scaleui';
import { Text, Strong, Flex, Tooltip } from '@scale/scaleui-radix';
import pluralize from 'pluralize';
import { isNil } from 'lodash';

export const EXIT_RETRY_THRESHOLD = 3;

export const ModelErrorButton = ({
  errorMessage,
  onRetry,
  retryCooldown,
  numRetries,
  exitTask,
  noPayForIncompleteTasks,
}: {
  errorMessage: string;
  onRetry: () => void;
  retryCooldown: number;
  numRetries?: number;
  exitTask?: () => void;
  noPayForIncompleteTasks?: boolean;
}) => {
  const defaultExitContent = (
    <Flex direction="column" gap="2" maxWidth="400px">
      <Text size="1">
        The model is offline! We have notified the client. Unfortunately, this task cannot proceed
        until the client restores the model. If you choose to exit this task, you will be
        automatically paid for the time you already spent on working on it. Please keep in mind you
        will not be able to exit more than 5 tasks in a one-hour period.
      </Text>
    </Flex>
  );
  const noPayExitContent = (
    <Flex direction="column" gap="2" maxWidth="400px">
      <Text size="1">
        <Strong>Service temporarily unavailable</Strong>
      </Text>
      <Text size="1">
        The model is currently offline and we're working to restore service.{' '}
        <Strong>Before leaving:</Strong> Copy and save your current attempt materials locally.
        <Strong>When service resumes:</Strong> Retry this task or start a new one.
      </Text>
      <Text size="1">
        <FontAwesomeIcon icon="triangle-exclamation" /> Please note:{' '}
        <Strong>You will not be compensated for tasks exited at this stage</Strong>. Please keep in
        mind you will not be able to exit more than 5 tasks in a one-hour period.
      </Text>
    </Flex>
  );
  const limitExceededMessage =
    "You've exceeded your limit for exiting tasks this hour. Try tasking on another project.";
  const limitExceededContent = <Text>{limitExceededMessage}</Text>;
  // TODO: Make this more robust as this is not ideal. Clean-up in follow-up PR when enabling pay for other incomple tasks cases
  const limitExceeded = errorMessage === limitExceededMessage;

  const exitContent = limitExceeded
    ? limitExceededContent
    : noPayForIncompleteTasks
    ? noPayExitContent
    : defaultExitContent;

  return (
    <div className="flex flex-col items-center gap-2 p-3">
      <div className="flex items-center text-danger-400">
        <Icon variant="danger" size="sm" icon="exclamation-circle" />
        {errorMessage}
      </div>
      <div className="flex space-x-2">
        <Button disabled={retryCooldown > 0} variant="warning" size="sm" onClick={onRetry}>
          {retryCooldown > 0
            ? `Please wait ${pluralize('second', retryCooldown, true)}`
            : 'Try Again'}
        </Button>
        {!isNil(numRetries) && numRetries >= EXIT_RETRY_THRESHOLD && exitTask && (
          <Tooltip content={exitContent} side="bottom">
            <Button variant="danger" disabled={limitExceeded} size="sm" onClick={exitTask}>
              Exit Task
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
